import { useCallback, useEffect, useRef, useState } from "react";
import { fetchActiveBroadcasts } from "../../queries/broadcastApi";

// Poll cadence for active broadcasts. Mirrors the useReleaseNotes shape but
// polls instead of firing once — a broadcast can be raised/lowered by an admin
// at any time, so the banner refreshes on an interval.
export const POLL_INTERVAL_MS = 60000;

// localStorage key for a per-broadcast dismissal. A dismissed id stays hidden
// across reloads; a NEW active id (never dismissed) still shows.
export const dismissedKey = (id) => `workspace.broadcast.${id}.dismissed`;

const isDismissed = (id) => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(dismissedKey(id)) === "true";
  } catch (_) {
    return false;
  }
};

const markDismissed = (id) => {
  try {
    window.localStorage.setItem(dismissedKey(id), "true");
  } catch (_) {
    /* SSR / storage-disabled — best effort */
  }
};

/**
 * Owns the system-broadcast poll + dismissal state (PLEX-4102).
 *
 * Fetches active broadcasts on mount and every `intervalMs`, tolerating
 * errors/empty (renders nothing). `visible` filters out any id the user has
 * dismissed — both in-session (state) and persisted (localStorage), so a
 * dismissed banner stays gone while a newly-raised one appears.
 */
export const useSystemBroadcast = (authData = null, { intervalMs = POLL_INTERVAL_MS } = {}) => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [dismissed, setDismissed] = useState(() => new Set());
  // Keep the latest authData in a ref so the interval callback isn't recreated
  // on every auth-token refresh (which would reset the poll clock).
  const authRef = useRef(authData);
  authRef.current = authData;

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchActiveBroadcasts(authRef.current)
        .then((resp) => {
          if (cancelled) return;
          const list = Array.isArray(resp?.broadcasts) ? resp.broadcasts : [];
          setBroadcasts(list.filter((b) => b && b.id != null && b.message));
        })
        .catch(() => {
          /* tolerate errors — leave the last good list, render nothing new */
        });
    };
    load();
    const timer = setInterval(load, intervalMs);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [intervalMs]);

  const dismiss = useCallback((id) => {
    markDismissed(id);
    setDismissed((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const visible = broadcasts.filter((b) => !dismissed.has(b.id) && !isDismissed(b.id));

  return { broadcasts, visible, dismiss };
};

export default useSystemBroadcast;
